/**
 * The card and the row every settings-shaped screen is built out of —
 * `design-remediation.md` WP2, drawn from
 * `21-catalog-packages/html/packages-account.html`.
 *
 * My account used to define its own `Field` and `KV` inside the page: a label
 * in 11px uppercase and a key/value line that shared nothing with the rest of
 * the product. The mockup's shape is not novel — a titled card with a sub, and
 * rows of "icon · name and explanation · value · button" — and it belongs
 * beside the other components rather than inside one page, because Security,
 * Preferences and (next) Organization all draw exactly it.
 *
 * `Card` in `primitives.tsx` is not this. That one is padded and holds
 * anything; this one is a header with a hairline under it and rows that run to
 * both edges, which is why it is `overflow-hidden` and why the padding lives
 * on the rows rather than the box.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn.js'

export function SettingsCard({ title, sub, action, children, className }: {
  title: string
  /** The 12px muted line beside the title, on the same baseline. */
  sub?: string
  /**
   * One control at the right of the header — **Add a method**, **Save**.
   *
   * On the header rather than in a row because it acts on the card, not on
   * any one line in it (board 2).
   */
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn('shrink-0 overflow-hidden rounded-record border border-line',
      'bg-surface', className)}>
      <div className={cn('flex items-baseline gap-2.5 border-b border-line-faint',
        'px-[18px] pb-3 pt-4')}>
        <h2 className="m-0 text-[14.5px] font-bold text-text">{title}</h2>
        {sub && <span className="text-[12px] text-text-muted">{sub}</span>}
        {action && <span className="ml-auto shrink-0 self-center">{action}</span>}
      </div>
      {children}
    </section>
  )
}

export interface SettingRowProps {
  /** A 17px lucide glyph, in its own 20px well so every label starts level. */
  icon?: ReactNode
  label: string
  /**
   * The line under the label. A node rather than a string since the Security
   * card puts a row of chips there — what two-step is *on with*, which is a
   * list and reads worse as a sentence.
   */
  sub?: ReactNode
  /** The state, right-aligned before the button. */
  value?: string
  /** `good` is the accent — "On · authenticator app", not "Off". */
  /**
   * `warn` is for a count running out — two recovery codes left is not bad
   * news yet, and it is not good news either (board 2).
   */
  tone?: 'plain' | 'good' | 'warn'
  /** One 30px button, or nothing. */
  action?: ReactNode
}

/**
 * One row.
 *
 * **It stacks below `sm`.** The label, the value and the button were all
 * competing for one 390px line, and `truncate` gave way first — a Sign-in
 * methods card whose rows read "Passw…" and "Recovery cod…". Under `sm` the
 * value and the button drop to their own line, indented past the icon well so
 * they still read as belonging to the row above them.
 */
export function SettingRow({ icon, label, sub, value, tone = 'plain', action }: SettingRowProps) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line-faint',
      'px-[18px] py-3 last:border-b-0')}>
      <span className="flex w-5 shrink-0 justify-center text-text-muted">{icon}</span>
      <div className="flex min-w-0 flex-1 basis-[calc(100%-2rem)] flex-col gap-px sm:basis-auto">
        <b className="truncate text-[13.5px] font-bold text-text">{label}</b>
        {/* `truncate` only for a string. A node here is the Security card's
            row of chips, and clipping those would hide a method. */}
        {sub && (typeof sub === 'string'
          ? <span className="truncate text-[12px] text-text-muted">{sub}</span>
          : <span className="text-[12px] text-text-muted">{sub}</span>)}
      </div>
      {(value || action) && (
        <span className="ml-8 flex shrink-0 items-center gap-3 sm:ml-0">
          {value && (
            <span className={cn('whitespace-nowrap text-[12.5px]',
              tone === 'good' ? 'font-semibold text-accent'
                : tone === 'warn' ? 'font-semibold text-warn'
                  : 'font-medium text-text-muted')}>
              {value}
            </span>
          )}
          {action}
        </span>
      )}
    </div>
  )
}
